import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useContentLibrary } from "@/data/content";

export default function CaseStudiesIndex() {
  const { data, loading } = useContentLibrary();
  const { caseStudies } = data;

  return (
    <div className="bg-white text-zinc-950 dark:bg-zinc-950 dark:text-zinc-100">
      <SEO
        title="Case Studies"
        description="How regulated enterprises use Jaisphere to ship AI safely, migrate to cloud without drama, and modernise systems they were told couldn't be touched."
        path="/case-studies"
      />
      <Nav />

      <section className="pt-32 md:pt-40 pb-16">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#002FA7] dark:text-[#7C9BFF]">
            Customer stories
          </span>
          <h1 className="mt-4 font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-zinc-950 dark:text-zinc-50">
            Real programs.
            <br />
            <span className="text-[#002FA7] dark:text-[#7C9BFF]">Measurable outcomes.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed">
            How regulated enterprises use Jaisphere to ship AI safely, migrate to cloud without
            drama, and modernise systems they were told couldn&apos;t be touched.
          </p>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12 space-y-6">
          {loading && <p className="text-zinc-600 dark:text-zinc-400">Loading case studies...</p>}
          {caseStudies.map((c, i) => (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.55, delay: i * 0.06 }}
            >
              <Link
                to={`/case-studies/${c.slug}`}
                data-testid={`case-study-card-${c.slug}`}
                className="group relative block border border-zinc-200 hover:border-zinc-950 rounded-sm p-8 md:p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_rgba(9,9,11,0.18)] dark:border-zinc-800 dark:hover:border-zinc-50"
              >
                <div className="grid grid-cols-12 gap-8 items-start">
                  <div className="col-span-12 lg:col-span-8">
                    <div className="flex flex-wrap items-center gap-3 text-[11px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
                      <span className="px-2 py-1 border border-zinc-200 dark:border-zinc-800">{c.industry}</span>
                      <span>{c.company}</span>
                    </div>
                    <h2 className="mt-5 font-display font-bold tracking-tight text-2xl sm:text-3xl text-zinc-950 dark:text-zinc-50 group-hover:text-[#002FA7] dark:group-hover:text-[#7C9BFF] transition-colors">
                      {c.title}
                    </h2>
                    <p className="mt-3 max-w-2xl text-zinc-600 dark:text-zinc-400 leading-relaxed">{c.summary}</p>

                    <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#002FA7] dark:text-[#7C9BFF]">
                      Read the case study
                      <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  </div>

                  <div className="col-span-12 lg:col-span-4 grid grid-cols-3 gap-3">
                    {c.metrics.map((m) => (
                      <div key={m.v} className="border border-zinc-200 dark:border-zinc-800 p-4 rounded-sm">
                        <div className="font-display font-black tracking-tighter text-xl sm:text-2xl text-zinc-950 dark:text-zinc-50">{m.k}</div>
                        <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400 leading-tight">{m.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
